import { createElement, useState, useRef, useEffect, HTMLProps, useLayoutEffect } from 'react';
import { SegmentedButtonProps, SegmentValuesType } from './';
import { getSegmentContent, isSegmentDisabled } from './__internal__';
import { Scrollable } from '../Scrollable';
import { ButtonVariant } from '../Button';
import { BaseButton } from '../Button/__internal__';
import { OverflowLite } from '@sas/react-svg';
import { MenuButton } from '../MenuButton';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useIsRTL } from '../../locale';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { ReactKeyboardEvent } from '../../react-synthetic-events';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { postfixTestId } from '../../util/testId';
import { setRef } from '../../util/ref-util';
import { isNullish } from '@sas/nova-core/typeguards';
import { getLogger } from '@sas/nova-core/logging';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-SegmentedButton-gui-icu.properties';

import { renderInlineCssVars, useDesignTokens } from '../../theme';
import cx from 'classnames';
import './SegmentedButton.css';
import cn from './SegmentedButton.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

const logger = getLogger('SegmentedButton');

export const SegmentedButton = ({
  focusable = true,
  orientation = 'horizontal',
  ...props
}: SegmentedButtonProps) => {
  const isVertical = orientation === 'vertical';

  const [focusedIndex, setFocusedIndex] = useState(props.selectedIndex ?? 0);
  const [scrollIndex, setScrollIndex] = useState(props.selectedIndex ?? 0);
  const [segmentMinWidth, setSegmentMinWidth] = useState<number>();
  const [shrinking, setShrinking] = useState(false);

  const buttonsRef = useRef<(HTMLElement | null)[]>([]);

  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const getIconA11yInfo = useIconA11yInfo();
  const narrator = useDirectSpeech();
  const prevSelectedIndex = usePreviousValue(props.selectedIndex);

  const scrollable = props.scrollable ?? (isVertical ? true : false);
  useEffect(() => {
    if (
      !isNullish(focusedIndex) &&
      buttonsRef.current?.includes(document.activeElement as HTMLElement)
    ) {
      buttonsRef.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  /** There are 4 cases when equal width comes with unscrollable
   * 1. The width of the segmentedbutton is auto and free to expand.
   * 2. The width of the segmentedbutton is auto but has a max width which is less than the new size after equal width
   * 3. The initial width of the button is not enough for the natural size
   * 4. The initial width of the button is enough for the natural size but less than the new size after equal width
   *
   * Case 3 could be detected by the compares between the client width and scroll width
   * But it is hard to distinguish case 1 and case 4 here. Just set min width for these cases.
   * Then case 2 could be detected by the compares between the previous and current client width
   * And it is approachable to find out the other cases in the useLayoutEffect.
   */
  const equalWidth = !props.variableWidth && !isVertical;
  const listWidthRef = useRef<number>(); // to tell it is case 2 or case 4.
  const [setContentRef, getContentRef] = useResizeObserverRef(({ target }) => {
    if (equalWidth && buttonsRef.current.length > 0) {
      const shrinking = !scrollable && target.clientWidth < target.scrollWidth; // case 2 (second time) and case 3
      setShrinking(shrinking);
      if (shrinking && listWidthRef.current && target.clientWidth > listWidthRef.current) {
        // case 2 (second time)
        setSegmentMinWidth(target.clientWidth / props.values.length);
        return;
      }
      if (!shrinking) {
        // case 1, case 2 (first time), case 4 (before equal width)
        const widestSegmentWidth = Math.max(
          ...buttonsRef.current.map(
            (button) =>
              Math.ceil(
                (button?.firstChild as Element | null | undefined)?.getBoundingClientRect().width ??
                  0
              ) +
              parseFloat(dt.spacing.core.s) * 16 * 2
            // button.clientWidth is incorrent in some case such as changing locale from zh-XX to en (1px shaking)
            // So use the text width plus the padding to get the correct button width
            // But it relies on button's padding using that spacing token
            // Thus this approach would break down if the button's padding were to change from inset-squish-s
          )
        );
        listWidthRef.current = target.clientWidth;
        if (widestSegmentWidth > 0) {
          setSegmentMinWidth(Math.min(listWidthRef.current, widestSegmentWidth));
          return;
        }
      }
    }
    setSegmentMinWidth(undefined);
  });
  useLayoutEffect(() => {
    const target = getContentRef();
    if (!equalWidth) {
      setSegmentMinWidth(undefined);
    } else if (
      !scrollable &&
      segmentMinWidth &&
      listWidthRef.current &&
      target &&
      target.clientWidth < target.scrollWidth
    ) {
      // After set min width and rerender, if the ul element is not truncated, it is case 1. Keep the min width
      // else if the client width is not changed, it is case 4 (after equal width). Remove the min width and mark shrink.
      if (target.clientWidth === listWidthRef.current) {
        setSegmentMinWidth(undefined);
        setShrinking(true);
      }
      // otherwise, the client width is larger, it is case 4. Deal with it in the resize handler.
    }
  }, [getContentRef, equalWidth, scrollable, segmentMinWidth]);

  function onSelectedIndex(selectedIndex: number) {
    if (props.deselectable && props.selectedIndex === selectedIndex) {
      props.onSelectedIndex?.(undefined);
    } else {
      props.onSelectedIndex?.(selectedIndex);
    }
    scrollable && setScrollIndex(selectedIndex); // Probably has been covered by onFocus of BaseButton and setting currentIndex of Scrollable.
    setFocusedIndex(selectedIndex); // Change the focused index as well when selecting a segment in the overflow menu.
    if (!props.deselectable) {
      const selectedValue = getSegmentContent(props.values[selectedIndex]);
      narrator?.notify(
        localize('segmented.button.selected.aria', {
          label:
            typeof selectedValue === 'string'
              ? selectedValue
              : getIconA11yInfo(selectedValue).ariaLabel ?? '',
        })
      );
    }
  }

  const onKeyDown = (event: ReactKeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown' /* falls-through */:
        event.preventDefault();
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (focusedIndex < props.values.length - 1) event.preventDefault();
        setFocusedIndex((prevIndex) => Math.min(props.values.length - 1, prevIndex + 1));
        break;
      case 'ArrowUp' /* falls-through */:
        event.preventDefault();
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (focusedIndex > 0) event.preventDefault();
        setFocusedIndex((prevIndex) => Math.max(0, prevIndex - 1));
        break;
      case 'Home':
        if (props.values.length > 0) {
          event.preventDefault();
          setFocusedIndex(0);
        }
        break;
      case 'End':
        if (props.values.length > 0) {
          event.preventDefault();
          setFocusedIndex(props.values.length - 1);
        }
        break;
      default:
        return; // NOTE: early return
    }
  };

  const buttonTokens = dt.enableNovaTokens ? dt.stateSecondaryNova : dt.stateSecondary;
  const stateTokens = dt.enableNovaTokens ? dt.stateStandardNova : dt.stateStandard;

  const segments = (props.values as SegmentValuesType).map((value, index) => {
    const selected = index === props.selectedIndex;
    const segmentContent = getSegmentContent(value);
    const ariaLabel =
      typeof segmentContent === 'string'
        ? segmentContent
        : getIconA11yInfo(segmentContent)?.ariaLabel;
    const disabled = props.disabled || isSegmentDisabled(value);

    if (
      (typeof segmentContent !== 'string' && isNullish(segmentContent.icon)) ||
      (typeof segmentContent === 'string' && segmentContent.trim() === '')
    ) {
      logger.error(
        'A text segment must have a nonempty value and An icon segment must have an icon'
      );
    }

    return (
      <BaseButton
        key={index}
        testId={postfixTestId(props.testId, `button-${index}`)}
        className={cx(cn.button, props.buttonClassName)}
        style={{
          ...renderInlineCssVars(
            {
              'focus-outline':
                selected && dt.themeVariant === 'high-contrast'
                  ? 'var(--focus-dark-bg)'
                  : 'var(--focus-light-bg)',
              'normal-bd': buttonTokens.normal.bd,
              'selected-bd': props.disabled
                ? buttonTokens.disabledSelected.bd
                : buttonTokens.selected.bd,
              'active-filter': disabled || selected ? 'none' : stateTokens.active.filter,
            },
            'button'
          ),
          minWidth: segmentMinWidth,
          ...props.buttonStyle?.({ index, selected }),
        }}
        variant={ButtonVariant.Standard}
        text={typeof segmentContent === 'string' ? segmentContent : undefined}
        icon={typeof segmentContent === 'string' ? undefined : segmentContent}
        disabled={disabled}
        focusable={focusable && index === focusedIndex}
        tokens={
          !selected
            ? undefined
            : ({ disabled }) => (disabled ? buttonTokens.disabledSelected : buttonTokens.selected)
        }
        onPress={() => onSelectedIndex(index)}
        onFocus={
          scrollable
            ? () => {
                /**
                 * Cases:
                 *  Selected the first segment, scroll to the end by clicking the scroll button
                 *    1. click the last segment
                 *    2. tab to focus out and back
                 *    3. shift tab to focus back to the segment
                 * Problem:
                 *  When focus index is different from scroll index
                 *  Browser would first scroll to the selected segment (focusedIndex) then Scrollable would scroll to the scrollIndex
                 * Fix:
                 *   set scroll index to the focused index when focus in the segment.
                 * Note:
                 *   It's used to sync the scrollIndex with focusedIndex in useEffect and focus-IN of Scrollable
                 *   focus-in of Scrollable cannot cover case 3 since scrollable was not focus out.
                 *      Browser would scroll to the focused one and Scrollable then scroll to the scroll index
                 *   on-focus of Scrollable would use previous focusedIndex (not changed yet) that breaks case 1.
                 *      Scrollable would scroll back to the first segment in on-focus of Scrollable then scroll to the last after selection
                 *
                 *   setScrollIndex in the onFocus of Button could use the latest focused index.
                 *   Then it also cover the setScrollIndex in useEffect.
                 */
                setScrollIndex(index);
              }
            : undefined
        }
        aria-pressed={props.deselectable ? selected : undefined}
        ariaLabel={
          !props.deselectable && selected && ariaLabel
            ? localize('segmented.button.selected.aria', {
                label: ariaLabel,
              })
            : undefined
        }
        ref={(button) => {
          buttonsRef.current[index] = button;
          if (index === focusedIndex) {
            setRef(props.focusedButtonRef, button);
          }
        }}
      />
    );
  });
  const contentProps: HTMLProps<HTMLUListElement> = {
    role: 'toolbar',
    onKeyDown,
    'aria-label': props.ariaLabel,
    'aria-labelledby': props.ariaLabelledBy,
  };
  return (
    <div
      className={cx(
        cn.componentContainer[orientation],
        equalWidth && cn.equalWidth,
        !scrollable && cn.noScroll
      )}
      style={{
        ...props.style,
        /**
         * When a horizontal segmented button is scrollable but width is very limited, the button container (<li />) has to be shrunk like
         * < BU... >
         * Thus the width of the li element is changed when the width is changed.
         * When the width of the li element reduced to be extremely small, the width of all the segments ( ul element, liWidth * n when equal width)
         * would be less than the Scrollable container and then the scroll buttons would be gone. Then goes into a infinite loop
         * So the li element has to have a shrunk limitation.
         * The exact limiation is 64 / (props.values.length - 1). 64 is the width of the two scroll buttons
         * But it is unnecessary to be shrunk smaller than an icon button so use 2rem (in scss) instead.
         * The only exception is the limitation is supposed to be 64 when the values length is 2
         */
        ...renderInlineCssVars(
          {
            'extreme-min-width': props.values.length === 2 ? '4rem' : '2rem',
          },
          'segmentedbutton'
        ),
      }}
      data-testid={props.testId}
    >
      {scrollable ? (
        <Scrollable
          testId={postfixTestId(props.testId, 'scrollable')}
          orientation={orientation}
          className={cn.scrollContainer}
          contentClassName={cn.contentContainer}
          itemClassName={(index) =>
            cx(
              cn.buttonContainer,
              index === props.selectedIndex && cn.selected,
              isSegmentDisabled(props.values[index]) && cn.disabled
            )
          }
          contentProps={contentProps}
          contentRef={setContentRef}
          // Do not scroll to the selected index when focus out since focusedIndex maybe different from scroll index
          suppressScrollOnBlur
          /**
           * It is supposed to avoid scroll when blur but respond to scroll when selected index is changed without focus in
           *
           * if the selected index is changed, like from overflow menu or interactive with other controls, scroll to the selected segment
           * else stay in the current index
           *
           * when changing the selected index when focus in, it is duplicate to setScrollIndex in onSelectedIndex
           *
           */
          currentIndex={
            !isNullish(props.selectedIndex) && prevSelectedIndex !== props.selectedIndex
              ? props.selectedIndex
              : scrollIndex
          }
          setCurrentIndex={setScrollIndex}
        >
          {segments}
        </Scrollable>
      ) : (
        <ul
          className={cx(
            cn.contentContainer,
            (!equalWidth || shrinking) &&
              (typeof props.values[0] === 'string' ? cn.shrinkingText : cn.shrinkingIcon)
          )}
          // cn.shrinking has a default min-width. It is only needed when the segmentedbutton is really shrinking.
          // Otherwise it may break the equal width in case 1 and 2 (segmentedbutton would be unable to expand when auto width).
          // Firefox cannot show the ellipsis when the button width is 2rem so change the min width for text button to 3rem.
          ref={setContentRef}
          {...contentProps}
        >
          {segments.map((segment, index) => {
            const selected = index === props.selectedIndex;
            return (
              <li
                key={index}
                role="presentation"
                className={cx(
                  cn.buttonContainer,
                  selected && cn.selected,
                  isSegmentDisabled(props.values[index]) && cn.disabled
                )}
                style={{
                  minWidth: props.buttonStyle?.({ index, selected })?.minWidth,
                }}
              >
                {segment}
              </li>
            );
          })}
        </ul>
      )}
      {props.enableOverflow ? (
        <MenuButton
          testId={postfixTestId(props.testId, 'overflowButton')}
          icon={{ icon: OverflowLite }}
          tooltip={props.overflowButtonTooltip ?? localize('overflow.button.tooltip.label')}
          ariaLabel={props.overflowButtonAriaLabel ?? localize('overflow.button.tooltip.label')}
          disabled={props.disabled || (props.values as SegmentValuesType).every(isSegmentDisabled)}
          variant={ButtonVariant.Icon}
          hideDownArrow
          menuAlign={{ horizontalAlign: isVertical ? 'start' : 'end', verticalAlign: 'end' }}
          sections={[
            {
              selectedIndex: props.selectedIndex ?? undefined,
              items: (props.values as SegmentValuesType).map((value, index) => {
                const segmentContent = getSegmentContent(value);
                return {
                  text:
                    typeof segmentContent === 'string'
                      ? segmentContent
                      : getIconA11yInfo(segmentContent).tooltip ?? '',
                  disabled: isSegmentDisabled(value),
                  testId: postfixTestId(props.testId, `menuItem-${index}`),
                };
              }),
            },
          ]}
          onItemSelected={(location) => {
            const selectedIndex = location[0][1];
            onSelectedIndex(selectedIndex);
          }}
        />
      ) : null}
    </div>
  );
};
